// Program Workroom — the "make things" half of the Contract Project workspace
// (blueprint §16 Copilot sidecar pattern, page-first). Chat is scoped to the
// program's backing zou contract project (program.zouProjectId): conversations
// persist as that project's chats, and the agent gets the contract-type tool
// set + project file context. Delivery-aware tools land later; the surface is
// functionally complete without them.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChatView } from '../../../../components/ChatView';
import { ChatExploreRail } from '../../../../components/ChatView/ChatExploreRail';
import { AssignedActionList } from '../../../../components/delivery/widgets/AssignedActionList';
import { PageSectionErrorBoundary } from '../../../../components/layout';
import { WorkroomProgramProvider } from '../../../../contexts/WorkroomProgramContext';
import { primeConversationCache } from '../../../../hooks/useConversation';
import { useCreateProjectChat, useProjectChats } from '../../../../hooks/useProjectChats';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { agentClient } from '../../../../lib/connect/clients';
import { convertProtoConversation } from '../../../../lib/connect/protoToChatHistory';
import { useLayoutStore } from '../../../../lib/stores/LayoutStore';
import { cn } from '../../../../lib/utils';
import { workroomChatReadOnly } from './workroomChatAccess';
const SIGNALS_ROW_CAP = 8;
const SIGNALS_TESTIDS = {
    container: Testids.workroom.signalsList,
    row: Testids.workroom.signalsRow,
    empty: Testids.workroom.signalsEmpty,
    error: Testids.workroom.signalsError,
    more: Testids.workroom.signalsMore
};
// The assigned-actions feed (SAD-170 ListMyAssignedActions) doesn't depend on
// a zou project binding at all — it must render for a program regardless of
// whether the chat surface beside it has anywhere to anchor conversations
// (SAD-179 / D15).
function SignalsPanel({ programId, assistOpen }) {
    return /*#__PURE__*/ _jsxs("aside", {
        className: cn('hidden w-80 flex-shrink-0 flex-col border-l min-h-0', !assistOpen && 'xl:flex'),
        "data-testid": Testids.workroom.signalsPanel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "p-4 border-b",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-sm font-semibold text-foreground",
                        children: "Assigned to you"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-xs text-muted-foreground",
                        children: "This program's deliverables, issues, and actions assigned to you."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 min-h-0 overflow-y-auto p-3",
                children: /*#__PURE__*/ _jsx(AssignedActionList, {
                    programId: programId,
                    cap: SIGNALS_ROW_CAP,
                    testids: SIGNALS_TESTIDS
                })
            })
        ]
    });
}
/** Strip the legacy `shanks-` prefix before any RPC call. */ function bareChatId(chatId) {
    return chatId.startsWith('shanks-') ? chatId.slice(7) : chatId;
}
// Same shape as ProjectChatView's history fetch: GetConversation via shanks,
// NotFound → empty thread for new-chat ergonomics.
async function fetchChatHistory(chatId, queryClient, knownNotOwner) {
    try {
        const resp = await agentClient.getConversation({
            id: bareChatId(chatId)
        });
        primeConversationCache(queryClient, chatId, resp);
        return {
            ...convertProtoConversation(resp.conversation, resp.messages),
            readOnly: workroomChatReadOnly({
                ok: true,
                readOnly: resp.readOnly
            })
        };
    } catch (err) {
        if (err instanceof ConnectError && err.code === Code.NotFound) {
            return {
                messages: [],
                readOnly: workroomChatReadOnly({
                    ok: false,
                    error: err
                }, knownNotOwner)
            };
        }
        throw err;
    }
}
function WorkroomChat({ chatId, projectId, knownNotOwner }) {
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: [
            'chat',
            chatId,
            {
                knownNotOwner
            }
        ],
        queryFn: ()=>fetchChatHistory(chatId, queryClient, knownNotOwner),
        staleTime: 0,
        refetchOnMount: 'always'
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex-1 flex items-center justify-center",
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
            })
        });
    }
    return /*#__PURE__*/ _jsx(ChatView, {
        chatId: chatId,
        projectId: projectId,
        initialMessages: data?.messages ?? [],
        // Explicit so ChatView skips useChatAccess, which reads a new chat's
        // NotFound as "not the owner" (SAD-756). A failed history load (no
        // data) stays read-only.
        readOnly: data?.readOnly ?? true,
        // S7 — render the conversation through the Specra chat-message frame.
        variant: "specra"
    }, chatId);
}
export function WorkroomPage() {
    const { program, programId, isLoading: programLoading } = useProgramContext();
    const zouProjectId = program?.zouProjectId ?? '';
    const unbound = !programLoading && !zouProjectId;
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedChatId = searchParams.get('chat') ?? '';
    const assistOpen = useLayoutStore((s)=>s.assistOpen);
    const createChat = useCreateProjectChat();
    // Zou lists every chat in the project (teammates' included), including a
    // freshly created one before shanks has any message for it.
    const projectChats = useProjectChats(zouProjectId || null, {
        sort: 'createdAt',
        order: 'desc'
    });
    const railChats = useMemo(()=>(projectChats.data?.chats ?? []).map((c)=>({
                chatId: c.chatId,
                title: c.title,
                createdAt: c.createdAt,
                updatedAt: c.createdAt,
                byline: c.isOwner ? 'Your chat' : c.createdBy.name
            })), [
        projectChats.data
    ]);
    const handleCreateChat = async ()=>{
        if (!zouProjectId) return;
        try {
            const response = await createChat.mutateAsync({
                projectId: zouProjectId,
                data: {}
            });
            setSearchParams({
                chat: response.chat.chatId
            });
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to create chat');
        }
    };
    return /*#__PURE__*/ _jsx(WorkroomProgramProvider, {
        value: {
            programId,
            zouProjectId
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full min-h-0 flex-col bg-background",
            children: [
                selectedChatId && /*#__PURE__*/ _jsx("div", {
                    className: "px-4 py-2",
                    children: /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        onClick: ()=>setSearchParams({}),
                        children: [
                            /*#__PURE__*/ _jsx(ArrowLeft, {
                                className: "size-4",
                                "aria-hidden": true
                            }),
                            "Workroom chats"
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-1 min-h-0 overflow-hidden bg-background",
                    "data-testid": Testids.workroom.page,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: selectedChatId ? 'hidden' : 'flex',
                            children: /*#__PURE__*/ _jsx(ChatExploreRail, {
                                scope: {
                                    projectId: zouProjectId,
                                    chats: railChats,
                                    isLoading: projectChats.isLoading,
                                    error: projectChats.error,
                                    refetch: projectChats.refetch,
                                    activeChatId: selectedChatId,
                                    onSelectChat: (chatId)=>setSearchParams({
                                            chat: chatId
                                        }),
                                    onNewChat: handleCreateChat,
                                    newChatDisabled: createChat.isPending,
                                    rootTestId: Testids.workroom.chatList,
                                    newChatTestId: Testids.workroom.newChat
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: cn('flex-1 min-w-0 flex flex-col min-h-0', !selectedChatId && 'hidden md:flex'),
                            children: unbound ? /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 flex items-center justify-center",
                                "data-testid": Testids.workroom.emptyState,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "text-center max-w-sm p-6",
                                    children: [
                                        /*#__PURE__*/ _jsx(MessageSquare, {
                                            className: "w-10 h-10 mx-auto mb-3 text-muted-foreground",
                                            "aria-hidden": true
                                        }),
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "text-lg font-semibold mb-1",
                                            children: "No workroom yet"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: "This program has no backing contract project, so there is nowhere to anchor its chats and artifacts."
                                        })
                                    ]
                                })
                            }) : selectedChatId ? /*#__PURE__*/ _jsx(PageSectionErrorBoundary, {
                                sectionName: "Workroom chat",
                                children: /*#__PURE__*/ _jsx(WorkroomChat, {
                                    chatId: selectedChatId,
                                    projectId: zouProjectId,
                                    // zou's record carries the creator; a NotFound from shanks
                                    // must not hand a composer to someone who isn't it.
                                    knownNotOwner: projectChats.data?.chats.find((c)=>c.chatId === selectedChatId)?.isOwner === false
                                })
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 flex items-center justify-center",
                                "data-testid": Testids.workroom.emptyState,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "text-center max-w-sm p-6",
                                    children: [
                                        /*#__PURE__*/ _jsx(MessageSquare, {
                                            className: "w-10 h-10 mx-auto mb-3 text-muted-foreground",
                                            "aria-hidden": true
                                        }),
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "text-lg font-semibold mb-1",
                                            children: "Program workroom"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-muted-foreground mb-4",
                                            children: "Chat with AI about this program — draft a PMP, build a plan, write a delivery email. Select a chat or start a new one."
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            onClick: handleCreateChat,
                                            disabled: !zouProjectId || createChat.isPending,
                                            children: "Start a chat"
                                        })
                                    ]
                                })
                            })
                        }),
                        !selectedChatId && /*#__PURE__*/ _jsx(SignalsPanel, {
                            programId: programId,
                            assistOpen: assistOpen
                        })
                    ]
                })
            ]
        })
    });
}
